import { useState, useEffect } from 'react';
import { trackPMCtaClicked } from '../utils/analytics';

const PM_URL = 'https://psychematrix.io';

// Every sidebar link carries the same UTM triple as the Results CTA, so PM's
// analytics can tell a desktop-panel visit from a results-screen visit and
// which card earned it. Bare hrefs used to make all of this read as "direct".
function pmLink(content: string): string {
  return `${PM_URL}?utm_source=wot&utm_medium=sidebar&utm_content=${content}`;
}

interface SidebarProps {
  position: 'left' | 'right';
  gameState?: { score?: number; realm?: string; accuracy?: number; avgSpeed?: number };
}

// ─── Ad Creative Library (from integration plan Section 9) ───

// Category 1: Product Features
const FEATURE_CARDS = [
  {
    id: 'f1_assessment',
    label: 'PSYCHEMATRIX',
    body: '50 questions.\n10 archetypes. 4 primary types.\nOne map that defines you.\n\nPsychometric profiling + behavioral analysis.',
    cta: '▸ BUILD YOUR MAP',
  },
  {
    id: 'f2_galaxy',
    label: 'CHARACTER TRAIT GALAXY',
    body: 'See your character as a galaxy of traits.\n\nEvery star opens into a story.',
    cta: '▸ START EXPLORING',
  },
  {
    id: 'f3_skillsync',
    label: 'SKILLSYNC',
    body: 'AI career intelligence.\n\nUpload your CV →\nGet market analysis →\nSee your strategic map.',
    cta: '▸ START ANALYSIS',
  },
  {
    id: 'f4_market',
    label: 'MARKET INTELLIGENCE',
    body: 'UX Designer in Berlin?\nData Analyst in Tokyo?\n\nCost of living, salary ranges, networking events — one report.',
    cta: '▸ SCAN YOUR MARKET',
  },
  {
    id: 'f5_partner',
    label: 'PARTNER FIT',
    body: 'Measure your compatibility with your co-founder.\n\nDo your strengths overlap or complement?',
    cta: '▸ TEST YOUR FIT',
  },
];

// Category 2: Cognitive Hooks
const HOOK_CARDS = [
  {
    id: 'h1_know_vs_are',
    label: '',
    body: 'Trivia tests what you KNOW.\n\nPsycheMatrix tests WHO you are.',
    cta: '▸ KNOW YOURSELF',
  },
  {
    id: 'h3_speed_accuracy',
    label: '',
    body: 'Fast or accurate?\n\nYour risk tolerance shapes your career strategy.\n\n10 dimensions. 4 character types.',
    cta: '▸ SEE YOUR PROFILE',
  },
  {
    id: 'h4_depth',
    label: '',
    body: 'This game scratches the surface.\n\nPsycheMatrix maps the person underneath.',
    cta: '▸ GO DEEPER',
  },
];

// Category 3: Identity Hooks
const SOCIAL_CARDS = [
  {
    id: 's1_social_proof',
    label: 'THE MAP',
    body: 'Your mind has a shape.\n\nMost people never see theirs.\n\nYou can.',
    cta: '▸ SEE YOUR MAP',
  },
  {
    id: 's2_archetype_dist',
    label: 'THE ARCHETYPES',
    body: 'Innovator. Connector.\nLeader. Mentor.\n\n10 archetypes, 4 core types.\n\nWhich are you?',
    cta: '▸ FIND YOURS',
  },
];

// Category 5: Philosophical / Editorial
const EDITORIAL_CARDS = [
  {
    id: 'p1_aristotle',
    label: '',
    body: '"Knowing yourself is the beginning of all wisdom."\n\n— Aristotle',
    cta: '▸ BEGIN',
  },
  {
    id: 'p2_ai_age',
    label: '',
    body: 'AI can learn everything.\n\nBut it doesn\'t know YOU.\n\nMap your cognitive DNA.',
    cta: '▸ YOUR DNA MAP',
  },
  {
    id: 'p3_career_2027',
    label: '',
    body: 'Which skills will survive 2027?\n\nWEF data + personal analysis = strategic roadmap.',
    cta: '▸ SET YOUR STRATEGY',
  },
];

// Left sidebar pool: Product Features + Social Proof
const LEFT_CARDS = [...FEATURE_CARDS, ...SOCIAL_CARDS];

// Right sidebar pool: Cognitive Hooks + Editorial
const RIGHT_CARDS = [...HOOK_CARDS, ...EDITORIAL_CARDS];

// Category 4: Score-Reactive (for right sidebar after game)
function getReactiveCard(gameState: SidebarProps['gameState']) {
  if (!gameState?.accuracy) return null;
  const acc = gameState.accuracy;
  const avg = gameState.avgSpeed;
  
  // R4 — Speed-based (< 3s avg)
  if (avg && avg < 3) return {
    id: 'r4_speed',
    label: '',
    // No invented trait correlations: speed is an observation, the full
    // read is PM's claim. (The old copy asserted "quick thinkers tend to
    // be the Innovator archetype", which nothing measures.)
    body: `FAST DECISION-MAKING.\n\nAvg response: ${avg.toFixed(1)}s\n\nSpeed is one dimension. PsycheMatrix reads how you decide, not just how fast.`,
    cta: '▸ SEE THE FULL READ',
  };

  // R1 — High Score (≥80%)
  if (acc >= 0.8) return {
    id: 'r1_high_score',
    label: '',
    body: `IMPRESSIVE.\n\n${Math.round(acc * 100)}% accuracy.\n\nYour knowledge runs deep. But where do you stand analytically?`,
    cta: '▸ BUILD YOUR PROFILE',
  };

  // R2 — Mid Score (40-79%)
  if (acc >= 0.4) return {
    id: 'r2_mid_score',
    label: '',
    body: `INTERESTING PROFILE.\n\n${Math.round(acc * 100)}% accuracy.\n\nSelective knowledge management. That's a personality trait.`,
    cta: '▸ WHAT\'S YOUR TYPE?',
  };

  // R3 — Low Score (<40%)
  return {
    id: 'r3_low_score',
    label: '',
    body: 'KNOWLEDGE ≠ INTELLIGENCE\n\nTrivia scores don\'t show your potential.\n\nDiscover your real strengths.',
    cta: '▸ FIND YOUR STRENGTH',
  };
}

export function PsycheMatrixSidebar({ position, gameState }: SidebarProps) {
  const pool = position === 'left' ? LEFT_CARDS : RIGHT_CARDS;
  const [activeIdx, setActiveIdx] = useState(0);

  // Rotate every 15 seconds (per plan spec)
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIdx(prev => (prev + 1) % pool.length);
    }, 15000);
    return () => clearInterval(interval);
  }, [pool.length]);

  // Right sidebar: show reactive card if game state available
  const reactiveCard = position === 'right' ? getReactiveCard(gameState) : null;
  const card = reactiveCard || pool[activeIdx];

  return (
    <aside className="pm-sidebar" data-position={position}>
      {/* Wordmark — matches actual PsycheMatrix landing page */}
      <div className="pm-sidebar-top">
        <a href={pmLink('wordmark')} target="_blank" rel="noopener noreferrer" className="pm-wordmark"
           onClick={() => trackPMCtaClicked('sidebar', 'wordmark')}>
          PSYCHEMATRIX
        </a>

        {/* Content card */}
        <div className="pm-sidebar-card" key={card.id}>
          {card.label && <span className="pm-card-label">┄ {card.label} ┄</span>}
          <div className="pm-card-body">
            {card.body.split('\n\n').map((paragraph, index) => (
              <p key={index} style={{ whiteSpace: 'pre-line' }}>{paragraph}</p>
            ))}
          </div>
          <a
            href={pmLink(card.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="pm-card-cta"
            onClick={() => trackPMCtaClicked('sidebar', card.id)}
          >
            {card.cta}
          </a>
        </div>

        {/* Dot indicators (only when not showing reactive) */}
        {!reactiveCard && (
          <div className="pm-sidebar-dots">
            {pool.map((_, i) => (
              <div 
                key={i} 
                className={`pm-dot ${i === activeIdx ? 'pm-dot-active' : ''}`}
                onClick={() => setActiveIdx(i)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Subtle bottom branding */}
      <div className="pm-sidebar-footer">
        <a href={pmLink('footer')} target="_blank" rel="noopener noreferrer"
           onClick={() => trackPMCtaClicked('sidebar', 'footer')}>
          psychematrix.io
        </a>
      </div>
    </aside>
  );
}
